import type { Rng } from '../rng';
import type { Rect } from '../math';
import type { Contour } from './flatten';

export interface OutlineSample {
  x: number;
  y: number;
  /** Unit tangent, following the contour direction. */
  tx: number;
  ty: number;
  /** Unit normal pointing away from the glyph interior. */
  nx: number;
  ny: number;
  /** Arc-length position along the whole outline, normalised to [0, 1). */
  t: number;
  /** Index of the contour this sample came from. */
  contour: number;
}

/**
 * Samples `n` points spaced evenly by arc length over all contours.
 *
 * Contours are concatenated into one virtual curve so a glyph's counters get their
 * fair share of samples proportional to their perimeter — which is what growth
 * algorithms seeded from the outline want.
 *
 * `orient` decides which side is "outside"; pass the field gradient so the result is
 * correct regardless of the font's contour winding direction.
 */
export function sampleOutline(
  contours: readonly Contour[],
  n: number,
  orient: (x: number, y: number) => readonly [number, number],
  offset = 0,
): OutlineSample[] {
  const out: OutlineSample[] = [];
  if (n <= 0 || contours.length === 0) return out;

  let total = 0;
  for (const c of contours) total += c.length;
  if (total <= 0) return out;

  const step = total / n;
  let target = offset * step;
  let walked = 0;
  let produced = 0;

  for (let ci = 0; ci < contours.length && produced < n; ci++) {
    const pts = contours[ci].points;
    const count = pts.length / 2;
    for (let i = 0; i < count && produced < n; i++) {
      const j = (i + 1) % count;
      const ax = pts[i * 2];
      const ay = pts[i * 2 + 1];
      const bx = pts[j * 2];
      const by = pts[j * 2 + 1];
      const segLen = Math.hypot(bx - ax, by - ay);
      if (segLen < 1e-9) continue;

      while (target < walked + segLen && produced < n) {
        const f = (target - walked) / segLen;
        const x = ax + (bx - ax) * f;
        const y = ay + (by - ay) * f;
        const tx = (bx - ax) / segLen;
        const ty = (by - ay) / segLen;
        // Perpendicular; flipped to agree with the outward direction of the field.
        let nx = -ty;
        let ny = tx;
        const g = orient(x, y);
        if (nx * g[0] + ny * g[1] < 0) {
          nx = -nx;
          ny = -ny;
        }
        out.push({ x, y, tx, ty, nx, ny, t: target / total, contour: ci });
        produced++;
        target += step;
      }
      walked += segLen;
    }
  }

  return out;
}

export type InteriorSampleMode = 'uniform' | 'grid' | 'poisson' | 'deep';

export interface InteriorSampleOptions {
  mode: InteriorSampleMode;
  bounds: Rect;
  isInside: (x: number, y: number) => boolean;
  /** Signed distance, negative inside — required by the `deep` mode. */
  distance: (x: number, y: number) => number;
  rng: Rng;
}

/**
 * Returns up to `n` points inside the glyphs.
 *
 * - `uniform` — plain rejection sampling; fastest, slightly clumpy.
 * - `grid`    — jittered lattice; even coverage, keeps a visible rhythm.
 * - `poisson` — Bridson blue-noise; even coverage with no lattice artefacts. The
 *               count is approximate because the disc radius fixes the density.
 * - `deep`    — biased towards the glyph core, giving a soft falloff at the edges.
 */
export function sampleInterior(n: number, opts: InteriorSampleOptions): Float32Array {
  const { bounds, isInside, rng } = opts;
  if (n <= 0 || bounds.width <= 0 || bounds.height <= 0) return new Float32Array(0);

  switch (opts.mode) {
    case 'grid':
      return sampleGrid(n, opts);
    case 'poisson':
      return samplePoisson(n, opts);
    case 'deep':
      return sampleDeep(n, opts);
    default: {
      const out = new Float32Array(n * 2);
      let count = 0;
      // Cap the attempts so a degenerate mask (e.g. a space-only text) cannot hang.
      const maxAttempts = n * 200 + 5000;
      for (let attempt = 0; attempt < maxAttempts && count < n; attempt++) {
        const x = bounds.x + rng.next() * bounds.width;
        const y = bounds.y + rng.next() * bounds.height;
        if (!isInside(x, y)) continue;
        out[count * 2] = x;
        out[count * 2 + 1] = y;
        count++;
      }
      return out.subarray(0, count * 2);
    }
  }
}

function sampleGrid(n: number, opts: InteriorSampleOptions): Float32Array {
  const { bounds, isInside, rng } = opts;
  // Size the lattice from the *filled* area so the first pass is already close, then
  // tighten if the mask turned out to be sparser than the probe suggested.
  const area = bounds.width * bounds.height * estimateFillRatio(opts);
  let spacing = Math.sqrt(area / Math.max(1, n));
  const out: number[] = [];

  for (let pass = 0; pass < 6; pass++) {
    out.length = 0;
    const cols = Math.max(1, Math.ceil(bounds.width / spacing));
    const rows = Math.max(1, Math.ceil(bounds.height / spacing));
    const cw = bounds.width / cols;
    const ch = bounds.height / rows;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = bounds.x + (c + 0.5 + rng.signed(0.35)) * cw;
        const y = bounds.y + (r + 0.5 + rng.signed(0.35)) * ch;
        if (isInside(x, y)) out.push(x, y);
      }
    }
    const got = out.length / 2;
    if (got >= n || got === 0) break;
    spacing *= Math.sqrt(got / n) || 0.7;
  }

  const result = new Float32Array(Math.min(n, out.length / 2) * 2);
  for (let i = 0; i < result.length; i++) result[i] = out[i];
  return result;
}

/**
 * Estimates the area actually covered by the glyphs, as a fraction of the bounding
 * box. Every density calculation needs this: the bounding box of "FORM" is mostly
 * empty, so sizing a disc radius from the box area misses by a factor of five.
 */
function estimateFillRatio(opts: InteriorSampleOptions, probes = 600): number {
  const { bounds, isInside, rng } = opts;
  let hits = 0;
  for (let i = 0; i < probes; i++) {
    if (isInside(bounds.x + rng.next() * bounds.width, bounds.y + rng.next() * bounds.height)) hits++;
  }
  // Never return 0 — a very thin hairline font could miss every probe, and the
  // callers would then divide by it.
  return Math.max(0.01, hits / probes);
}

/**
 * Bridson, "Fast Poisson Disk Sampling in Arbitrary Dimensions" (SIGGRAPH 2007),
 * restricted to the glyph interior.
 *
 * Two deviations from the textbook version, both forced by the shape of a text mask:
 *
 *  - The disc radius is derived from the *filled* area, not the bounding box, or the
 *    point count comes out several times too low.
 *  - The active list is seeded from a coarse grid rather than a single point. Letters
 *    are disconnected regions; a single seed only ever floods the glyph it landed in,
 *    leaving every other letter empty.
 */
function samplePoisson(n: number, opts: InteriorSampleOptions): Float32Array {
  const { bounds, isInside, rng } = opts;
  // Poisson-disc packing fills roughly 70 % of the available area at radius r.
  const area = bounds.width * bounds.height * estimateFillRatio(opts);
  const radius = Math.sqrt((area * 0.7) / Math.max(1, n));
  const k = 20;

  const cell = radius / Math.SQRT2;
  const cols = Math.ceil(bounds.width / cell) + 1;
  const rows = Math.ceil(bounds.height / cell) + 1;
  const grid = new Int32Array(cols * rows).fill(-1);

  const px: number[] = [];
  const py: number[] = [];
  const active: number[] = [];

  const gridIndex = (x: number, y: number): number => {
    const gx = Math.floor((x - bounds.x) / cell);
    const gy = Math.floor((y - bounds.y) / cell);
    return gy * cols + gx;
  };

  const fits = (x: number, y: number): boolean => {
    if (x < bounds.x || y < bounds.y || x >= bounds.x + bounds.width || y >= bounds.y + bounds.height) return false;
    const gx = Math.floor((x - bounds.x) / cell);
    const gy = Math.floor((y - bounds.y) / cell);
    for (let yy = Math.max(0, gy - 2); yy <= Math.min(rows - 1, gy + 2); yy++) {
      for (let xx = Math.max(0, gx - 2); xx <= Math.min(cols - 1, gx + 2); xx++) {
        const idx = grid[yy * cols + xx];
        if (idx < 0) continue;
        const dx = px[idx] - x;
        const dy = py[idx] - y;
        if (dx * dx + dy * dy < radius * radius) return false;
      }
    }
    return true;
  };

  const add = (x: number, y: number): void => {
    const idx = px.length;
    px.push(x);
    py.push(y);
    grid[gridIndex(x, y)] = idx;
    active.push(idx);
  };

  // Seed every disconnected region: walk a coarse jittered lattice and admit any
  // cell centre that is inside the mask and respects the disc radius.
  const seedStep = radius * 2;
  const seedCols = Math.max(1, Math.ceil(bounds.width / seedStep));
  const seedRows = Math.max(1, Math.ceil(bounds.height / seedStep));
  // Guard against pathological parameter combinations producing a huge seed lattice.
  if (seedCols * seedRows <= 4_000_000) {
    for (let r = 0; r < seedRows; r++) {
      for (let c = 0; c < seedCols; c++) {
        const x = bounds.x + (c + 0.5 + rng.signed(0.3)) * seedStep;
        const y = bounds.y + (r + 0.5 + rng.signed(0.3)) * seedStep;
        if (isInside(x, y) && fits(x, y)) add(x, y);
      }
    }
  }
  if (px.length === 0) {
    for (let attempt = 0; attempt < 4000 && px.length === 0; attempt++) {
      const x = bounds.x + rng.next() * bounds.width;
      const y = bounds.y + rng.next() * bounds.height;
      if (isInside(x, y)) add(x, y);
    }
  }
  if (px.length === 0) return new Float32Array(0);

  while (active.length > 0 && px.length < n * 4) {
    const pick = rng.int(0, active.length - 1);
    const idx = active[pick];
    let placed = false;
    for (let i = 0; i < k; i++) {
      const angle = rng.next() * Math.PI * 2;
      const r = radius * (1 + rng.next());
      const x = px[idx] + Math.cos(angle) * r;
      const y = py[idx] + Math.sin(angle) * r;
      if (!isInside(x, y) || !fits(x, y)) continue;
      add(x, y);
      placed = true;
      break;
    }
    if (!placed) {
      active[pick] = active[active.length - 1];
      active.pop();
    }
  }

  const count = Math.min(n, px.length);
  const out = new Float32Array(count * 2);
  // Take a random subset rather than the first `count`, which would bias towards the
  // seed point's neighbourhood.
  const order = rng.shuffle([...px.keys()]);
  for (let i = 0; i < count; i++) {
    out[i * 2] = px[order[i]];
    out[i * 2 + 1] = py[order[i]];
  }
  return out;
}

function sampleDeep(n: number, opts: InteriorSampleOptions): Float32Array {
  const { bounds, isInside, distance, rng } = opts;
  const out = new Float32Array(n * 2);
  let count = 0;

  // Find the deepest point in a quick probe pass so the acceptance ratio is scaled.
  let deepest = 1;
  for (let i = 0; i < 400; i++) {
    const x = bounds.x + rng.next() * bounds.width;
    const y = bounds.y + rng.next() * bounds.height;
    if (isInside(x, y)) deepest = Math.max(deepest, -distance(x, y));
  }

  const maxAttempts = n * 300 + 5000;
  for (let attempt = 0; attempt < maxAttempts && count < n; attempt++) {
    const x = bounds.x + rng.next() * bounds.width;
    const y = bounds.y + rng.next() * bounds.height;
    if (!isInside(x, y)) continue;
    const depth = -distance(x, y) / deepest;
    if (rng.next() > depth) continue; // rejection sampling against the depth profile
    out[count * 2] = x;
    out[count * 2 + 1] = y;
    count++;
  }
  return out.subarray(0, count * 2);
}
